import { aparenciaRotulo, lerNumeroRisco, type Citado, type LimiteConcentracao, type Produto } from '../../../dominio/produtos'
import { formatarDistancia } from '../../../dominio/unidades'
import { ValorCitado } from './ValorCitado'

const numero = (valor: number, casas = 2) => valor.toLocaleString('pt-BR', { maximumFractionDigits: casas })

function Citavel({ citado, unidade = '', casas }: { citado: Citado | null; unidade?: string; casas?: number }) {
  if (!citado) return <span className="ficha-produto__ausente">não se aplica / sem dado</span>
  return (
    <ValorCitado fonte={citado.fonte} observacao={citado.observacao}>
      {numero(citado.valor, casas)}
      {unidade && ` ${unidade}`}
    </ValorCitado>
  )
}

function Limite({ limite }: { limite: LimiteConcentracao | null }) {
  if (!limite) return <span className="ficha-produto__ausente">não estabelecido</span>
  return (
    <ValorCitado fonte={limite.fonte} observacao={limite.observacao}>
      {numero(limite.valor)} {limite.unidade === 'mg/m3' ? 'mg/m³' : 'ppm'}
    </ValorCitado>
  )
}

const ESTADO: Record<Produto['estadoTransporte'], string> = {
  gasLiquefeitoPressurizado: 'gás liquefeito sob pressão',
  gasComprimido: 'gás comprimido',
  liquido: 'líquido',
}

/**
 * Ficha do produto: identificação, propriedades que decidem a resposta e as
 * distâncias iniciais do GRE — cada número com a fonte.
 */
export function FichaProduto({ produto, secoes = ['identificacao', 'propriedades', 'gre', 'atencao'] }: {
  produto: Produto
  secoes?: ReadonlyArray<'identificacao' | 'propriedades' | 'gre' | 'atencao'>
}) {
  const leitura = lerNumeroRisco(produto.numeroRisco.valor)
  const { gre } = produto
  return (
    <div className="ficha-produto">
      {secoes.includes('identificacao') && (
        <section>
          <h3 className="titulo-controle">Identificação</h3>
          <dl className="leituras">
            <div><dt>Número ONU</dt><dd>{produto.onu}</dd></div>
            <div>
              <dt>Número de risco</dt>
              <dd><ValorCitado fonte={produto.numeroRisco.fonte}>{produto.numeroRisco.valor}</ValorCitado></dd>
            </div>
            <div className="leituras__largo"><dt>Leitura do painel</dt><dd>{leitura.resumo}</dd></div>
            <div>
              <dt>Classe</dt>
              <dd>
                <ValorCitado fonte={produto.classe.fonte}>
                  {produto.classe.valor} — {aparenciaRotulo(produto.classe.valor).nome}
                </ValorCitado>
              </dd>
            </div>
            {produto.riscosSubsidiarios.length > 0 && (
              <div>
                <dt>Riscos subsidiários</dt>
                <dd>{produto.riscosSubsidiarios.map((c) => `${c} — ${aparenciaRotulo(c).nome}`).join('; ')}</dd>
              </div>
            )}
            <div><dt>Guia GRE</dt><dd><Citavel citado={produto.guiaGre} casas={0} /></dd></div>
            <div><dt>Transportado como</dt><dd>{ESTADO[produto.estadoTransporte]}</dd></div>
          </dl>
        </section>
      )}

      {secoes.includes('propriedades') && (
        <section>
          <h3 className="titulo-controle">Propriedades</h3>
          <dl className="leituras">
            <div><dt>Densidade do vapor (ar = 1)</dt><dd><Citavel citado={produto.densidadeVaporRelativa} /></dd></div>
            <div><dt>LII / LSI</dt><dd>{produto.liiPorcento ? <><Citavel citado={produto.liiPorcento} unidade="%" /> / <Citavel citado={produto.lsiPorcento} unidade="%" /></> : <span className="ficha-produto__ausente">não inflamável</span>}</dd></div>
            <div><dt>IDLH</dt><dd><Limite limite={produto.idlh} /></dd></div>
            {produto.aegl60 ? (
              (['aegl1', 'aegl2', 'aegl3'] as const).map((nivel, i) => (
                <div key={nivel}><dt>AEGL-{i + 1} (60 min)</dt><dd><Limite limite={produto.aegl60![nivel]} /></dd></div>
              ))
            ) : (
              <div><dt>AEGL (60 min)</dt><dd><span className="ficha-produto__ausente">não estabelecido</span></dd></div>
            )}
            <div><dt>Pressão de vapor</dt><dd><Citavel citado={produto.pressaoVaporKPa} unidade="kPa" casas={4} /></dd></div>
            <div><dt>Calor de combustão (PCI)</dt><dd><Citavel citado={produto.calorCombustaoMJKg} unidade="MJ/kg" casas={1} /></dd></div>
          </dl>
        </section>
      )}

      {secoes.includes('gre') && (
        <section>
          <h3 className="titulo-controle">Distâncias iniciais — GRE, guia {produto.guiaGre.valor}</h3>
          <dl className="leituras">
            <div>
              <dt>Isolamento inicial (todas as direções)</dt>
              <dd><ValorCitado fonte={gre.isolamentoInicialM.fonte} observacao={gre.isolamentoInicialM.observacao}>{formatarDistancia(gre.isolamentoInicialM.valor)}</ValorCitado></dd>
            </div>
            {gre.evacuacaoGrandeVazamentoM && (
              <div>
                <dt>Grande vazamento: evacuar a favor do vento</dt>
                <dd><ValorCitado fonte={gre.evacuacaoGrandeVazamentoM.fonte} observacao={gre.evacuacaoGrandeVazamentoM.observacao}>{formatarDistancia(gre.evacuacaoGrandeVazamentoM.valor)}</ValorCitado></dd>
              </div>
            )}
            <div>
              <dt>Tanque envolvido em fogo: isolar</dt>
              <dd><ValorCitado fonte={gre.isolamentoTanqueEmFogoM.fonte} observacao={gre.isolamentoTanqueEmFogoM.observacao}>{formatarDistancia(gre.isolamentoTanqueEmFogoM.valor)}</ValorCitado></dd>
            </div>
            {gre.tabela1Pequeno && (
              <div className="leituras__largo">
                <dt>Tabela 1 — pequeno vazamento (isolar · proteger dia · noite)</dt>
                <dd>
                  <ValorCitado fonte={gre.tabela1Pequeno.fonte} observacao={gre.tabela1Pequeno.observacao}>
                    {formatarDistancia(gre.tabela1Pequeno.isolarM)} · {formatarDistancia(gre.tabela1Pequeno.protegerDiaM)} · {formatarDistancia(gre.tabela1Pequeno.protegerNoiteM)}
                  </ValorCitado>
                </dd>
              </div>
            )}
            {gre.tabela3CaminhaoTanque && (
              <div className="leituras__largo">
                <dt>Tabela 3 — caminhão-tanque, grande vazamento: isolar</dt>
                <dd>
                  <ValorCitado fonte={gre.tabela3CaminhaoTanque.fonte} observacao={gre.tabela3CaminhaoTanque.observacao}>
                    {formatarDistancia(gre.tabela3CaminhaoTanque.isolarM)}; proteger até {formatarDistancia(gre.tabela3CaminhaoTanque.protegerNoite.ventoBaixoM)} (noite, vento baixo)
                  </ValorCitado>
                </dd>
              </div>
            )}
          </dl>
        </section>
      )}

      {secoes.includes('atencao') && produto.atencao.length > 0 && (
        <section>
          <h3 className="titulo-controle">Atenção</h3>
          <ul className="ficha-produto__atencao">
            {produto.atencao.map((item) => <li key={item}>{item}</li>)}
          </ul>
        </section>
      )}
    </div>
  )
}
